import type { RefCallback } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useResizeObserver } from './useResizeObserver';

export type UseOverflowObserverOptions = {
  /** Gap in px between items — must match the container's flex gap. @default 4 */
  gap?: number;
};

export type VisibleCountInput = {
  /** Natural width of each item, in render order. */
  itemWidths: number[];
  /** Width of always-shown leading content (0 if none). */
  leadingWidth: number;
  /** Width of the "+N" overflow chip. */
  chipWidth: number;
  /** Gap between adjacent items. */
  gap: number;
  /** Available container width. */
  available: number;
};

/**
 * Greedily counts how many leading items fit `available`, reserving room for the
 * "+N" chip for every count except showing them all (no overflow → no chip).
 * Pure and exhaustively testable; the early break makes a loop the right tool.
 */
export function computeVisibleCount({
  itemWidths,
  leadingWidth,
  chipWidth,
  gap,
  available,
}: VisibleCountInput): number {
  const hasLeading = leadingWidth > 0;
  let used = leadingWidth;
  let count = 0;

  for (let i = 0; i < itemWidths.length; i += 1) {
    const lead = count > 0 || hasLeading ? gap : 0;
    const isLast = i === itemWidths.length - 1;
    const reserve = isLast ? 0 : chipWidth + gap;
    if (used + lead + itemWidths[i] + reserve > available) {
      break;
    }
    used += lead + itemWidths[i];
    count += 1;
  }

  return count;
}

/** Natural widths captured once per `signature`, reused across resizes. */
type Measured = {
  signature: string;
  itemWidths: number[];
  leadingWidth: number;
  chipWidth: number;
};

/**
 * Counts how many leading items fit the container width, collapsing the rest
 * behind a "+N" chip. Attach `containerRef` to the row, `registerItem(i)` to
 * each visible item, `registerChip` to the "+N" element, `registerLeading` to
 * any always-shown leading content. Item widths are measured once per
 * `signature` and cached, so resizes recount without re-rendering (KIR-2546).
 */
export function useOverflowObserver(
  itemCount: number,
  signature: string,
  options?: UseOverflowObserverOptions
): {
  containerRef: RefCallback<HTMLElement>;
  registerItem: (index: number) => RefCallback<HTMLElement>;
  registerChip: RefCallback<HTMLElement>;
  registerLeading: RefCallback<HTMLElement>;
  visibleCount: number;
} {
  const gap = options?.gap ?? 4;
  const { resizeRef: containerRef, width } = useResizeObserver();
  const itemsRef = useRef<HTMLElement[]>([]);
  const chipRef = useRef<HTMLElement | null>(null);
  const leadingRef = useRef<HTMLElement | null>(null);
  const measuredRef = useRef<Measured | null>(null);
  const [visibleCount, setVisibleCount] = useState(itemCount);
  const [prevSignature, setPrevSignature] = useState(signature);

  // Adjust state during render (React's recommended pattern over a setState in
  // an effect): when the item set changes, show every item again so the next
  // layout pass can read natural widths while all are mounted. The stale
  // measurement is replaced in the effect (its signature no longer matches).
  // Runs pre-commit, so there is no extra paint.
  if (prevSignature !== signature) {
    setPrevSignature(signature);
    setVisibleCount(itemCount);
  }

  const registerItem = useCallback(
    (index: number): RefCallback<HTMLElement> =>
      (node): void => {
        if (node) {
          itemsRef.current[index] = node;
        }
      },
    []
  );

  const registerChip = useCallback<RefCallback<HTMLElement>>(node => {
    chipRef.current = node;
  }, []);

  const registerLeading = useCallback<RefCallback<HTMLElement>>(node => {
    leadingRef.current = node;
  }, []);

  useLayoutEffect(() => {
    if (width === 0) {
      return;
    }

    // Drop refs to unmounted items (a shrunk set leaves stale nodes parked at
    // higher indices) so nothing below reads a detached node.
    itemsRef.current.length = itemCount;

    // First pass for this signature: every item is mounted (the during-render
    // reset showed them all), so read each natural width once and cache it.
    if (!measuredRef.current || measuredRef.current.signature !== signature) {
      measuredRef.current = {
        signature,
        itemWidths: itemsRef.current.map(el => el.offsetWidth),
        leadingWidth: leadingRef.current?.offsetWidth ?? 0,
        // Upper-bound chip width so we never under-reserve and overflow the row.
        chipWidth: chipRef.current?.offsetWidth ?? 0,
      };
    }

    const measured = measuredRef.current;
    const count = computeVisibleCount({
      itemWidths: measured.itemWidths,
      leadingWidth: measured.leadingWidth,
      chipWidth: measured.chipWidth,
      gap,
      available: width,
    });
    setVisibleCount(prev => (prev === count ? prev : count));
  }, [width, signature, itemCount, gap]);

  return {
    containerRef,
    registerItem,
    registerChip,
    registerLeading,
    visibleCount,
  };
}
